import Head from "next/head";
import Link from "next/link";
import { Box, Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question:
      "How do I claim my tickets? When does the opt in open before each game?",
    answer:
      "You will receive text when the window to opt-in opens. The opt-in window starts at 9 am on the first game day of a home series. You will have an opportunity to opt-in for all the games in the series and purchase guest tickets as well. The opt-in window closes 3 hours prior to game start.",
  },
  {
    question:
      "Do I get a ticket to each game on the schedule? Are there penalties if I do not opt in?",
    answer:
      "Tickets are guaranteed for each game on the schedule. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "Your tickets will be sent over 3 hours prior to game start and you can find the tickets within your Ballpark App.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "3 hours prior to game start you will receive a text confirming that tickets have been delivered to your Ballpark App account.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations will vary by game.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Summer Pass tickets, as well as seat upgrades and guest passes, are not transferable and not eligible for resale. Abusing these guidelines can result in your Summer Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <Link href="mailto:tickets@dbacks.com">tickets@dbacks.com</Link> for any
        assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer:
      "You can link your pass with other pass holders using Sit With a Friend located within the portal on the left navigation. This allows you to create and join ticket groups. All fans within a ticket group will be assigned seats next to each other.",
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets will be available for select games. If available, guest tickets are available for purchase when you opt into games.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        Please contact{" "}
        <Link href="mailto:tickets@dbacks.com">tickets@dbacks.com</Link> for any
        requests for refunds or cancellations.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from the Arizona Diamondbacks. You may unsubscribe at
      any time. By checking this box you agree to receive additional commercial
      emails from the Arizona Diamondbacks and MLB. The terms and conditions of
      your ticket and policies applicable to its use are updated to address
      circumstances related to COVID-19 and health and safety. By clicking the
      Purchase Tickets button, you agree to accept all{" "}
      <Link
        href={`${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`}
        target="_blank"
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target="_blank"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
        }
        target="_blank"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Diamondbacks account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only. MLB and supported mobile device required.
    </>
  );
};

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "Arizona Diamondbacks Terms and Conditions",
      onClick: () => {
        window.open(
          `${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`,
          "_self"
        );
      },
    },
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();

  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            `${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        Arizona Diamondbacks Terms and Conditions
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        id={`${TEAM_SCHEMA}-google-tag-manager`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
              (function(w,d,s,l,i){
                w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
                var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
                j.async=true;
                // j.src= 'https://url.us.m.mimecastprotect.com/s/_puYC73WLLSZyv0DC8fMIoKC0I'+i+dl;
                j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
                f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-3CSM3P');
              console.log('======azdbacks google tag manager loaded=====');
          `,
        }}
      />

      <noscript id={`${TEAM_SCHEMA}-google-tag-manager-noscript`}>
        <iframe
          src="https://url.us.m.mimecastprotect.com/s/LXVqC82B66fYQlAoU1hYIy5fRN"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>

      <script
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1532627770340759');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />

      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-tik-tok-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !(function (w, d, t) { 
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);
                (ttq.methods = [
                  "page",
                  "track",
                  "identify",
                  "instances",
                  "debug",
                  "on",
                  "off",
                  "once",
                  "ready",
                  "alias",
                  "group",
                  "enableCookie",
                  "disableCookie",
                ]),
                  (ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                  });
                for (var i = 0; i < ttq.methods.length; i++)
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                (ttq.instance = function (t) {
                  for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
                    ttq.setAndDefer(e, ttq.methods[n]);
                  return e;
                }),
                  (ttq.load = function (e, n) {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    (ttq._i = ttq._i || {}),
                      (ttq._i[e] = []),
                      (ttq._i[e]._u = i),
                      (ttq._t = ttq._t || {}),
                      (ttq._t[e] = +new Date()),
                      (ttq._o = ttq._o || {}),
                      (ttq._o[e] = n || {});
                    n = document.createElement("script");
                    (n.type = "text/javascript"),
                      (n.async = !0),
                      (n.src = i + "?sdkid=" + e + "&lib=" + t);
                    e = document.getElementsByTagName("script")[0];
                    e.parentNode.insertBefore(n, e);
                  });

                ttq.load("CBP9I5JC77UFL42EF2PG");
                ttq.page();
                console.log('tiktok pixel page view fired.');
              })(window, document, "ttq");

              window.addEventListener("${constants.customEvents.purchaseSuccess}", async function(e) {
                const { cartRes, checkoutRes, passes, user } = e.detail;

                ttq.identify({
                  email: user.email,
                  phone_number: "+1" + user.phoneNumber,
                });

                if(cartRes.products && cartRes.products.length) {
                  for(let product of cartRes.products){
                    ttq.track("CompletePayment", {
                      content_id: product.eventCode,
                      content_name: product.productTitle,
                      quantity: String(product.seatCount),
                      price: String(product.price),
                      value: String(product.price * product.seatCount),
                      currency: "USD",
                    });
                    await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                  }
                };

                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_id = passDetail.eventCode;
                  content_name = passDetail.passTitle;
                  price = String(passDetail.price);
                  quantity = String(cartRes.passes[0].seatCount);

                  ttq.track("CompletePayment", {
                    content_id: passDetail.eventCode,
                    content_name: passDetail.passTitle,
                    quantity: String(cartRes.passes[0].seatCount),
                    price: String(passDetail.price),
                    value: String(cartRes.totalAmount),
                    currency: "USD",
                  });
                };
                console.log('tik tok pixel purchase event fired.');
              });
            `,
        }}
      />

      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1532627770340759&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const HomePageZeroStateDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      You can purchase one{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.landing,
          query: { teamSchema },
        }}
      >
        here
      </Link>
      , or check your{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.purchase_history,
          query: { teamSchema },
        }}
      >
        Purchase History
      </Link>{" "}
      to see passes you’ve gifted.
    </Box>
  );
};

export const RedeemGiftSuccessModalDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      Your gift is all set! Create a{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.my_group,
          query: { teamSchema },
        }}
      >
        group
      </Link>{" "}
      to bring friends along, or explore the schedule in{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.home,
          query: { teamSchema },
        }}
      >
        Upcoming Games
      </Link>
      . We’ll send you an SMS when check-in opens as the season gets closer.
    </Box>
  );
};
